import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import { Tooltip } from "@fluentui/react-components";
import { CloudArrowUpFilled, DeleteFilled } from "@fluentui/react-icons";
import { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";

import {
  humanReadableFileSize,
  readFileAsDataUrl,
} from "../../../../../utils/file-utils.js";
import { getImageDimensionsFromDataUrl } from "../../../../../utils/image.js";

const MAX_FILE_SIZE_BYTES = 2_097_152;
const MAX_DIMENSION_PX = 2000;
const ACCEPTED_MIME_TYPES = new Set(["image/jpeg", "image/png"]);

interface TeamLogoDropzoneProps {
  disabled: boolean;
  errorMessage: string | null;
  onDeleteChange: () => void;
  onErrorMessageChange: (message: string | null) => void;
  onLogoPreviewUrlChange?: (url: string | null) => void;
  onSelectedFileChange: (file: File | null) => void;
  previewUrl?: string | null;
}

export const TeamLogoDropzone = ({
  disabled,
  errorMessage,
  onDeleteChange,
  onErrorMessageChange,
  onLogoPreviewUrlChange,
  onSelectedFileChange,
  previewUrl = null,
}: TeamLogoDropzoneProps) => {
  const [selectedPreviewUrl, setSelectedPreviewUrl] = useState<string | null>(
    null
  );

  useEffect(() => {
    setSelectedPreviewUrl(null);
  }, []);

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const [file] = acceptedFiles;
      onSelectedFileChange(null);
      setSelectedPreviewUrl(null);
      if (!file) {
        onErrorMessageChange("Please select a JPG or PNG file.");
        return;
      }

      if (!ACCEPTED_MIME_TYPES.has(file.type)) {
        onErrorMessageChange("Only JPG and PNG files are allowed.");
        return;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        onErrorMessageChange(
          `File must be ${humanReadableFileSize(MAX_FILE_SIZE_BYTES)} or smaller.`
        );
        return;
      }

      onErrorMessageChange(null);

      try {
        const dataUrl = await readFileAsDataUrl(file);
        const { width, height } = await getImageDimensionsFromDataUrl(dataUrl);

        if (width > MAX_DIMENSION_PX || height > MAX_DIMENSION_PX) {
          onErrorMessageChange(
            `Logo dimensions must be ${MAX_DIMENSION_PX}x${MAX_DIMENSION_PX} pixels or smaller.`
          );
          return;
        }

        onSelectedFileChange(file);
        setSelectedPreviewUrl(dataUrl);
        onLogoPreviewUrlChange?.(dataUrl);
      } catch {
        onErrorMessageChange("Failed to read the logo file.");
      }
    },
    [onErrorMessageChange, onLogoPreviewUrlChange, onSelectedFileChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
    },
    disabled,
    maxFiles: 1,
    multiple: false,
    onDrop: handleDrop,
  });

  const {
    role: _role,
    tabIndex: _tabIndex,
    ...dropzoneButtonProps
  } = getRootProps();

  const handleDelete = useCallback(() => {
    onSelectedFileChange(null);
    setSelectedPreviewUrl(null);
    onLogoPreviewUrlChange?.(null);
    onErrorMessageChange(null);
    onDeleteChange();
  }, [
    onDeleteChange,
    onErrorMessageChange,
    onLogoPreviewUrlChange,
    onSelectedFileChange,
  ]);

  const displayPreviewUrl = selectedPreviewUrl ?? previewUrl;
  const helperText = `Max size ${humanReadableFileSize(MAX_FILE_SIZE_BYTES)}. Max dimensions ${MAX_DIMENSION_PX}x${MAX_DIMENSION_PX} px.`;
  const handleClick154 = useStableCallback((event) => {
    event.stopPropagation();
    handleDelete();
  });

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="text-bzd-gray-500 text-sm">Office logo</div>
      <button
        {...dropzoneButtonProps}
        className="border-bzd-gray-300 bg-bzd-gray-50 hover:border-bzd-gray-500 relative flex min-h-60 w-full items-center justify-center overflow-hidden rounded-md border border-dashed transition-colors"
        disabled={disabled}
        type="button"
      >
        {displayPreviewUrl ? (
          <img
            alt="Logo Preview"
            className="h-auto w-full object-contain"
            height={400}
            src={displayPreviewUrl}
            width={640}
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-3 text-center">
            <div className="text-bzd-gray-600 flex flex-col items-center gap-1 text-sm">
              <CloudArrowUpFilled className="text-bzd-gray-500 size-20" />
              <div>
                {isDragActive
                  ? "Drop the file here..."
                  : "Drag and drop a JPG or PNG, or click to browse."}
              </div>
            </div>
          </div>
        )}
        {Boolean(displayPreviewUrl) && (
          <Tooltip content="Delete office logo" relationship="label">
            <button
              className="text-bzd-gray-600 absolute top-2 right-2 flex size-8 items-center justify-center rounded-full bg-white/80 shadow-sm transition-colors hover:bg-white hover:text-red-600 disabled:opacity-50"
              disabled={disabled}
              onClick={handleClick154}
              type="button"
            >
              <DeleteFilled className="size-4" />
            </button>
          </Tooltip>
        )}
      </button>
      <input {...getInputProps()} className="hidden" />
      <div className="text-bzd-gray-600 text-center text-xs">
        Drag and drop a JPG or PNG, or click to browse. {helperText}
      </div>
      {Boolean(errorMessage) && (
        <div className="text-sm text-red-600">{errorMessage}</div>
      )}
    </div>
  );
};
